import React, { useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Autocomplete,
  Checkbox,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ReactQuill from 'react-quill';
import { StatusDropdownType } from './Notifycandidate';
import { PannelMembersType } from '../../CandidateContent/CandidateContent';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';

interface CandidateTabContentProps {
  email: string | null;
  status: string | null;
  setEmail: (value: string) => void;
  subject: string;
  message: string;
  handleStatusChange: (event: SelectChangeEvent<string>) => void;
  setSubject: (value: string) => void;
  setMessage: (value: string) => void;
  statusDropdown: StatusDropdownType[];
  pannelMembersdropdown: PannelMembersType[];
  selectedPanelMembers: string[];
  setSelectedPanelMembers: (value: string[]) => void;
  candidateStatus: string | null;
  setStatus: (value: string | null) => void;
  isEditEmail: boolean;
  setIsEditEmail: (value: boolean) => void;
  editEmail: string;
  handleEmailChange: (newEmail: string) => void;
  handleUndoEmailEdit: () => void;
  handleSaveEmail: () => void;
  emailError: string;
}

const CandidateTabContent: React.FC<CandidateTabContentProps> = ({
  email,
  status,
  subject,
  message,
  handleStatusChange,
  setSubject,
  setMessage,
  statusDropdown,
  pannelMembersdropdown,
  selectedPanelMembers,
  setSelectedPanelMembers,
  candidateStatus,
  setStatus,
  setEmail,
  isEditEmail,
  setIsEditEmail,
  editEmail,
  handleEmailChange,
  handleUndoEmailEdit,
  handleSaveEmail,
  emailError,
}) => {
  // Dummy panel members for demo
  const panelMembers = ['John Doe', 'Jane Smith', 'Ravi Kumar'];

  useEffect(() => {
    setStatus(candidateStatus);
  }, [candidateStatus]);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const showPanelDropdown = status?.toLowerCase().includes('interview');
  return (
    <>
      {/* Communication Medium */}

      <TextField
        label="Email"
        size="small"
        value={editEmail}
        fullWidth
        margin="dense"
        required
        onChange={(e) => handleEmailChange(e.target.value)}
        error={!!emailError}
        helperText={emailError}
        InputProps={{
          endAdornment: isEditEmail ? (
            <InputAdornment position="end">
              <IconButton onClick={handleUndoEmailEdit}>
                <ClearIcon fontSize="small" titleAccess="Undo" />
              </IconButton>
              <IconButton onClick={handleSaveEmail}>
                <DoneIcon fontSize="small" titleAccess="Save" />
              </IconButton>
            </InputAdornment>
          ) : (
            <InputAdornment position="end" >
              <IconButton onClick={() => { setIsEditEmail(true) }}>
                <EditIcon fontSize="small" titleAccess='Edit' />
              </IconButton>

            </InputAdornment>
          ),
          readOnly: !isEditEmail,
        }}
      />

      {/* Status Dropdown */}
      {statusDropdown.length > 0 && (
        <FormControl fullWidth margin="dense" sx={{ mt: '2vh' }} required>
          <InputLabel size="small">Status</InputLabel>
          <Select
            size="small"
            label="Status"
            value={status || ''}
            onChange={handleStatusChange}
          >
            {statusDropdown.map((option) => (
              <MenuItem key={option.StageOrder} value={option.CandidateStatusKey}>
                {option.CandidateStatusKey}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showPanelDropdown && (
        <FormControl fullWidth margin="dense" sx={{ mt: '2vh' }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={pannelMembersdropdown.filter(
              (member) => member.RoleName?.toLowerCase() === 'panel member'
            )}
            getOptionLabel={(option) => option.EmailId}
            value={[]} // hide selected chips
            onChange={(_, newValue) => {
              // nothing here: handled below
            }}
            renderOption={(props, option) => {
              const isSelected = selectedPanelMembers.includes(option.EmailId);

              return (
                <li
                  {...props}
                  onClick={() => {
                    const updated = isSelected
                      ? selectedPanelMembers.filter((email) => email !== option.EmailId)
                      : [...selectedPanelMembers, option.EmailId];
                    setSelectedPanelMembers(updated);
                  }}
                >
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={isSelected}
                  />
                  <Typography variant="body2">{option.EmailId}</Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Panel Member"
                placeholder="Select Panel Members"
                size="small"
              />
            )}
            noOptionsText="No panel members available"
          />
        </FormControl>
      )}





      {/* Subject */}
      <TextField
        label="Subject"
        size="small"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        fullWidth
        sx={{ marginY: '2vh' }}
        required
        error={!subject}
        helperText={!subject ? 'Subject is required' : ''}
      />

      {/* Message */}
      <ReactQuill
        value={message}
        onChange={setMessage}
        style={{ height: '28vh', marginTop: '1vh' }}
        modules={{
          toolbar: [['bold', 'italic', 'link', { list: 'ordered' }, { list: 'bullet' }]],
        }}
      />
    </>
  );
};

export default CandidateTabContent;
